import {
  createFileRoute,
  Outlet,
  redirect,
  retainSearchParams,
  useNavigate,
  useRouter,
  useRouterState,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import { usePostHog } from '@posthog/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { sanitizeNextPath } from '@/lib/nextPath';
import ActivityBar from './-components/ActivityBar';
import { AssistantSidebar } from '../components/assistant-ui/assistant-sidebar';
import { Toaster } from '../components/ui/sonner';
import { useStore } from '../stores/useStore';
import { PRODUCTS } from '@/config/products';
import { EmailVerificationNudge } from './-components/EmailVerificationNudge';
import { AppCommands } from '@/components/commands/AppCommands';
import { addExceptionStep } from '@/lib/errorTracking';

function AppLayout() {
  const { isAuthenticated, isEmailVerified, user, profile, canManageSession } =
    useAuth();
  const chatOpen = useStore((s) => s.chatOpen);
  const posthog = usePostHog();
  const router = useRouter();

  // Track pageviews on route changes
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Eagerly warm the three product roots (route chunk + loader-warmed query
  // cache) once the shell mounts, so the first cross-product navigation lands
  // on content instead of a spinner — without waiting for hover intent
  // (`defaultPreload: 'intent'`). Deferred to idle so it never competes with
  // the initial route's own render/data. preloadRoute runs each route's loader
  // (best-effort warms via ensureQueryData); failures are swallowed since the
  // route's own component owns retries. `/meshgen` + `/diagnosis` share the
  // listGeometries cache entry, so that warm is paid once.
  //
  // `GET /subscription` was warmed here too, for a nav lock that was never
  // built: the query had no `useQuery` consumer anywhere, so the read fired on
  // every shell mount and was only ever written to cache. Removed in ENG-1141 —
  // the entitlement reads now live on the account route, beside the surface
  // that displays them.
  useEffect(() => {
    const warmRoots = () => {
      for (const p of PRODUCTS) {
        void router.preloadRoute({ to: p.route }).catch(() => undefined);
      }
    };
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(warmRoots);
      return () => window.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(warmRoots, 0);
    return () => window.clearTimeout(id);
  }, [router]);

  // Self-serve checkout is temporarily disabled while accounts are provisioned
  // manually, so we no longer gate app access on subscription state here.
  useEffect(() => {
    posthog.capture('$pageview', { $current_url: window.location.href });
    addExceptionStep(`Navigated to ${pathname}`, { category: 'navigation' });
  }, [pathname, posthog]);

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    // Use backend-canonical user_id so PostHog (analytics, replay, error
    // tracking, logs) and API logs join on one identity regardless of the
    // Stytch subject. The display fields
    // come from `profile`; gating the whole call on `user` is what keeps the id
    // canonical.
    // The PostHog `organization` group is keyed by the Khorium org UUID because
    // that is `organizations.id` in the org/subscription tables exported to the
    // PostHog warehouse, so events join there; name/plan come from that join.
    posthog.identify(user.id, { email: profile.email, name: profile.name });
    posthog.group('organization', user.organization.id);
  }, [isAuthenticated, user, profile, posthog]);

  return (
    // `CommandsProvider` lives at the ROOT (`__root.tsx`), not here — the
    // comment there says why. `AppCommands` stays: the global-scope HANDLERS
    // belong to the authenticated shell, only the registry is app-wide.
    <>
      {/* `h-dvh`, not `h-screen`: `100vh` on a mobile browser is the viewport
          with the URL bar RETRACTED, so the shell is taller than what is on
          screen and its bottom edge sits under the browser chrome. The chat
          composer is pinned to that edge, which made the one control the whole
          panel exists for the one control you could not reach. `dvh` tracks
          the visible viewport and is identical to `vh` on a desktop browser,
          so this changes nothing above `md`. */}
      <div className="flex h-dvh bg-muted text-foreground">
        <Toaster />
        <AppCommands />
        {/* Auth flows in as props — same shape as EmailVerificationNudge below.
            Keeps the bar renderable (and storyable) without an auth provider. */}
        <ActivityBar canManageSession={canManageSession} />
        <div className="flex flex-1 flex-col overflow-hidden">
          {!isEmailVerified && <EmailVerificationNudge />}
          <div className="min-h-0 flex-1">
            <AssistantSidebar isOpen={chatOpen}>
              <Outlet />
            </AssistantSidebar>
          </div>
        </div>
      </div>
    </>
  );
}

function AppErrorBoundary({ error }: ErrorComponentProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const message =
    error instanceof Error ? error.message : t('auth.appShell.errorFallback');

  return (
    <div className="flex h-screen items-center justify-center bg-muted">
      <div className="max-w-md px-6 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
          <span className="text-3xl text-destructive-strong">!</span>
        </div>
        <h2 className="mb-2 text-xl font-semibold text-foreground">
          {t('auth.appShell.errorTitle')}
        </h2>
        <p className="mb-6 text-muted-foreground">{message}</p>
        <button
          type="button"
          onClick={() => navigate({ to: '/meshgen' })}
          className="rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
        >
          {t('auth.appShell.goToDashboard')}
        </button>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_app')({
  validateSearch: (search: Record<string, unknown>): { chat?: string } => ({
    chat:
      typeof search.chat === 'string' && search.chat ? search.chat : undefined,
  }),
  search: { middlewares: [retainSearchParams(['chat'])] },
  beforeLoad: ({ context, location }) => {
    // Provider SDK hydration has not resolved yet. `isAuthenticated` is false
    // during that window for a perfectly valid stored session, so deciding
    // here bounces the user to /login and the round trip back paints the login
    // screen mid-boot. Defer instead: `main.tsx` invalidates the matches once
    // hydration settles, so this re-runs with a real verdict. Same shape as the
    // `/oauth/authorize` guard.
    if (context.auth.isLoading) return;
    if (!context.auth.isAuthenticated) {
      // Preserve the requested URL so login can bounce back to it.
      throw redirect({
        to: '/login',
        search: { next: sanitizeNextPath(location.href) },
      });
    }
  },
  component: AppLayout,
  errorComponent: AppErrorBoundary,
});
